import React, { useEffect, useState } from "react";

const MIN = 400;
const MAX = 2400;
const STEP = 50;
const FULL_SENTINEL = MAX + STEP;

type ViewWidthValue = number | "full";

interface ViewWidthResult {
  maxWidth: string | undefined;
  slider: React.ReactElement | null;
}

export function useViewWidth(storageKey: string): ViewWidthResult {
  const [value, setValue] = useState<ViewWidthValue>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === null || raw === "full") return "full";
      const n = parseInt(raw, 10);
      if (!Number.isFinite(n)) return "full";
      return Math.min(MAX, Math.max(MIN, n));
    } catch {
      return "full";
    }
  });

  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia("(max-width: 639px)").matches
  );

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, String(value));
    } catch {}
  }, [storageKey, value]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  if (isMobile) return { maxWidth: undefined, slider: null };

  const maxWidth = value === "full" ? undefined : `${value}px`;

  const slider = (
    <div className="flex items-center gap-2 text-xs text-gray-500 select-none">
      <span>Width</span>
      <input
        type="range"
        min={MIN}
        max={FULL_SENTINEL}
        step={STEP}
        value={value === "full" ? FULL_SENTINEL : value}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          setValue(n >= FULL_SENTINEL ? "full" : n);
        }}
        className="w-32 accent-blue-600"
        title="View width"
      />
      <span className="w-12 tabular-nums text-right">
        {value === "full" ? "Full" : `${value}px`}
      </span>
    </div>
  );

  return { maxWidth, slider };
}
